import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormDateField,
  FormInput,
  FormPrice,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { differenceInMinutes, format } from "date-fns";
import {
  AlertTriangleIcon,
  Calendar,
  ClockIcon,
  MapPinIcon,
  Timer,
  UserIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";
import type { RoomConflictResultDTO } from "@/modules/classes/domain/contracts/sessions";
import { classesApi } from "@/modules/classes/presentation/api/client";
import { useFindAlternativeRooms } from "@/modules/classes/presentation/api/queries";
import { useRoomQuery } from "@/modules/master/presentation/api/queries";
import { config } from "@/shared/lib/config";
import {
  addMinutesToTimeHhMm,
  formatDate,
  formatTime,
  toTimeHHmmss,
} from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  formatSlotDurationLaoLabel,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  formatQuarterHhMm,
  isCanonicalQuarterHhMm,
  parseQuarterClock,
} from "@/shared/lib/quarter-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  RoomBookingChargeTypeRadio,
  type RoomBookingChargeType,
} from "@/shared/ui/RoomBookingChargeTypeRadio";
import {
  SellPackageSettlementModeRadio,
  type SellPackageSettlementMode,
} from "@/shared/ui/SellPackageSettlementModeRadio";
import type {
  CreateRoomBookingDTO,
  UpdateRoomBookingDTO,
} from "../../domain/contracts/room-bookings";
import {
  AssistInstructorMultiSelect,
  type AssistInstructorSelection,
} from "./AssistInstructorMultiSelect";

type RoomItem = { id: string; name: string };

function toTimeHHmm(s: string | undefined): string {
  if (!s) return "09:00";
  if (s.length >= 8) return s.slice(0, 5);
  return s;
}

function toQuarterHhMmInitial(s: string | undefined): string {
  const raw = toTimeHHmm(s);
  const { h, m } = parseQuarterClock(raw);
  return formatQuarterHhMm(h, m);
}

function inferSlotDurationMinutesFromRange(
  initialStart?: string,
  initialEnd?: string,
): PrivateLessonSlotDurationMinutes {
  if (!initialStart?.trim() || !initialEnd?.trim()) return 60;
  const s = toQuarterHhMmInitial(initialStart);
  const e = toQuarterHhMmInitial(initialEnd);
  try {
    const mins = differenceInMinutes(
      new Date(`2000-01-01T${e}`),
      new Date(`2000-01-01T${s}`),
    );
    if (mins > 0 && PRIVATE_LESSON_SLOT_DURATION_SET.has(mins)) {
      return mins as PrivateLessonSlotDurationMinutes;
    }
  } catch {
    /* ignore */
  }
  return 60;
}

const RoomBookingFormSchema = z
  .object({
    roomId: z.string().min(1, "ຕ້ອງເລືອກຫ້ອງ"),
    date: z.string().min(1, "ຕ້ອງເລືອກວັນທີ"),
    startTime: z.string().min(1, "ຕ້ອງເລືອກເວລາເລີ່ມຕົ້ນ"),
    slotDurationMinutes: z.union([
      z.literal(15),
      z.literal(30),
      z.literal(45),
      z.literal(60),
      z.literal(75),
      z.literal(90),
      z.literal(105),
      z.literal(120),
      z.literal(135),
      z.literal(150),
      z.literal(165),
      z.literal(180),
    ]),
    contactName: z.string().optional(),
    contactPhone: z.string().optional(),
    title: z.string().optional(),
    chargeType: z.enum(["free", "paid"]),
    amount: z.number().int().min(0).optional().nullable(),
    settlementMode: z.enum(["receipt", "credit"]).default("receipt"),
    amountPaidNow: z.coerce.number().int().min(0).default(0),
    notes: z.string().optional(),
    changeReason: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!isCanonicalQuarterHhMm(data.startTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເວລາເລີ່ມຕົ້ນຕ້ອງເປັນນາທີ 00 / 15 / 30 / 45",
        path: ["startTime"],
      });
    }
  })
  .refine(
    (data) => {
      if (data.chargeType !== "paid") return true;
      return data.amount != null && data.amount >= 0;
    },
    {
      message: "ກະລຸນາກຳນົດຈຳນວນເງິນເມື່ອເລືອກຄິດເງິນ",
      path: ["amount"],
    },
  );

export type RoomBookingFormValues = z.infer<typeof RoomBookingFormSchema>;

type RoomBookingFormProps = {
  mode: "create" | "edit";
  /** `endTime` ໃນຄ່າເລີ່ມຕົ້ນໃຊ້ຄຳນວນຄວາມຍາວ (ຈາກ API ຫຼື calendar) ເທົ່ານັ້ນ — ບໍ່ເປັນຟິລດ໌ຟອມໂດຍກົງ */
  initialValues?: Partial<RoomBookingFormValues> & { endTime?: string };
  excludeRoomBookingId?: string;
  onSubmit: (values: CreateRoomBookingDTO | UpdateRoomBookingDTO) => void;
  submitting?: boolean;
};

export function RoomBookingForm({
  mode,
  initialValues,
  excludeRoomBookingId,
  onSubmit,
  submitting,
}: RoomBookingFormProps) {
  const methods = RHF.useForm<RoomBookingFormValues>({
    resolver: zodResolver(RoomBookingFormSchema),
    defaultValues: {
      roomId: initialValues?.roomId ?? "",
      date: initialValues?.date ?? format(new Date(), "yyyy-MM-dd"),
      startTime: toQuarterHhMmInitial(initialValues?.startTime ?? "09:00"),
      slotDurationMinutes: inferSlotDurationMinutesFromRange(
        initialValues?.startTime,
        initialValues?.endTime,
      ),
      contactName: initialValues?.contactName ?? "",
      contactPhone: initialValues?.contactPhone ?? "",
      title: initialValues?.title ?? "",
      chargeType: initialValues?.chargeType ?? "free",
      amount: initialValues?.amount ?? null,
      settlementMode: "receipt" as const,
      amountPaidNow: 0,
      notes: initialValues?.notes ?? "",
      changeReason: initialValues?.changeReason ?? "",
    },
  });

  const startTime = methods.watch("startTime");
  const slotDurationMinutes = methods.watch("slotDurationMinutes");
  const chargeType = methods.watch("chargeType");
  const settlementMode = methods.watch("settlementMode");
  const amountPaidNowW = methods.watch("amountPaidNow");
  const amountW = methods.watch("amount");
  const roomId = methods.watch("roomId");
  const date = methods.watch("date");
  const title = methods.watch("title");
  const contactName = methods.watch("contactName");

  const { data: room } = useRoomQuery(roomId || "");

  const [conflictResult, setConflictResult] =
    useState<RoomConflictResultDTO | null>(null);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);
  const findAlternatives = useFindAlternativeRooms();
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [assistInstructors, setAssistInstructors] = useState<
    AssistInstructorSelection[]
  >([]);

  const computedEndTime = useMemo(() => {
    if (!startTime?.trim()) return "";
    if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(slotDurationMinutes)) return "";
    return addMinutesToTimeHhMm(startTime, slotDurationMinutes);
  }, [startTime, slotDurationMinutes]);

  const durationMinutes = useMemo(
    () =>
      PRIVATE_LESSON_SLOT_DURATION_SET.has(slotDurationMinutes)
        ? slotDurationMinutes
        : 0,
    [slotDurationMinutes],
  );

  const durationLabel = useMemo(
    () => formatSlotDurationLaoLabel(durationMinutes),
    [durationMinutes],
  );

  const assistTotalPerSession = useMemo(
    () =>
      assistInstructors.reduce(
        (sum, a) => sum + Math.max(0, a.pricePerSession),
        0,
      ),
    [assistInstructors],
  );
  const roomAmount =
    chargeType === "paid" && amountW != null ? Math.max(0, amountW) : 0;
  const billingTotal = roomAmount + assistTotalPerSession;

  const roomName = room?.name ?? "";

  const handleSubmit = async (values: RoomBookingFormValues) => {
    const endHhMm = addMinutesToTimeHhMm(
      values.startTime,
      values.slotDurationMinutes,
    );
    const start = toTimeHHmmss(values.startTime);
    const end = toTimeHHmmss(endHhMm);
    setIsCheckingConflict(true);
    try {
      const result = await classesApi.sessions.checkConflict({
        roomId: values.roomId,
        date: values.date,
        startTime: start,
        endTime: end,
        ...(excludeRoomBookingId ? { excludeRoomBookingId } : {}),
      });
      if (result.hasConflict) {
        setConflictResult(result);
        try {
          await findAlternatives.mutateAsync({
            date: values.date,
            startTime: start,
            endTime: end,
            excludeRoomId: values.roomId,
          });
          setShowAlternatives(true);
        } catch (err) {
          console.error("Failed to find alternatives:", err);
        }
        return;
      }
    } catch (err) {
      console.error("Failed to check conflict:", err);
    } finally {
      setIsCheckingConflict(false);
    }
    setConflictResult(null);
    setShowAlternatives(false);

    if (
      mode === "create" &&
      values.chargeType === "paid" &&
      values.settlementMode === "credit" &&
      values.amount != null &&
      values.amount > 0
    ) {
      const apn = values.amountPaidNow ?? 0;
      if (apn > values.amount) {
        methods.setError("amountPaidNow", {
          type: "manual",
          message: "ບໍ່ເກີນຈຳນວນເງິນທັງໝົດ",
        });
        return;
      }
    }

    const payload: CreateRoomBookingDTO | UpdateRoomBookingDTO = {
      roomId: values.roomId,
      date: values.date,
      startTime:
        values.startTime.length === 5
          ? `${values.startTime}:00`
          : values.startTime,
      endTime: endHhMm.length === 5 ? `${endHhMm}:00` : endHhMm,
      contactName: values.contactName || undefined,
      contactPhone: values.contactPhone || undefined,
      title: values.title || undefined,
      chargeType: values.chargeType,
      amount:
        values.chargeType === "paid" && values.amount != null
          ? values.amount
          : undefined,
      notes: values.notes || undefined,
    };
    if (mode === "edit") {
      (payload as UpdateRoomBookingDTO).amount =
        values.chargeType === "paid" ? (values.amount ?? undefined) : null;
      (payload as UpdateRoomBookingDTO).changeReason =
        values.changeReason?.trim() || undefined;
    } else {
      const createPayload = payload as CreateRoomBookingDTO;
      if (values.chargeType === "paid") {
        createPayload.settlementMode = values.settlementMode ?? "receipt";
        if (createPayload.settlementMode === "credit" && values.amount != null) {
          createPayload.amountPaidNow = Math.min(
            Math.max(0, values.amountPaidNow ?? 0),
            values.amount,
          );
        }
      }
      if (assistInstructors.length > 0) {
        createPayload.assistInstructors = assistInstructors.map((a) => ({
          instructorId: a.id,
          pricePerSession: a.pricePerSession,
        }));
      }
    }
    onSubmit(payload);
  };

  const handleFindAlternatives = async () => {
    if (
      !date ||
      !startTime?.trim() ||
      !roomId ||
      !PRIVATE_LESSON_SLOT_DURATION_SET.has(slotDurationMinutes)
    ) {
      return;
    }
    const endHhMm = addMinutesToTimeHhMm(startTime, slotDurationMinutes);
    try {
      await findAlternatives.mutateAsync({
        date,
        startTime: toTimeHHmmss(startTime),
        endTime: toTimeHHmmss(endHhMm),
        excludeRoomId: roomId,
      });
      setShowAlternatives(true);
    } catch (err) {
      console.error("Failed to find alternatives:", err);
    }
  };

  const handleSelectAlternativeRoom = (altRoomId: string) => {
    methods.setValue("roomId", altRoomId);
    setShowAlternatives(false);
    setConflictResult(null);
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <FormRoot
          methods={methods}
          onSubmit={handleSubmit}
          className="flex flex-col gap-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormInfiniteCombobox<RoomItem>
              name="roomId"
              label="ຫ້ອງ"
              requiredMark
              queryKey={["master", "rooms", "lookup", "room-booking-form"]}
              queryFn={({ search, pageParam }) =>
                fetchLookupForInfinite<RoomItem>(
                  `${config.apiUrl}/master/rooms/lookup`,
                  { search, pageParam },
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem<RoomItem>(
                  `${config.apiUrl}/master/rooms/lookup`,
                  id,
                ).then((item) => item ?? null)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກຫ້ອງ"
            />

            <FormDateField
              name="date"
              label="ວັນທີ"
              requiredMark
              className="w-full"
            />

            <div className="grid grid-cols-1 gap-4 sm:col-span-2 lg:grid-cols-4">
              <div className="space-y-2">
                <div className="flex items-center gap-1 font-medium text-sm leading-none">
                  <span>ເວລາເລີ່ມຕົ້ນ</span>
                  <span aria-hidden className="text-red-500">
                    *
                  </span>
                </div>
                <RHF.Controller
                  name="startTime"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <>
                      <QuarterHourMinuteSelect
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                      {fieldState.error?.message ? (
                        <p className="text-red-500 text-xs">
                          {fieldState.error.message}
                        </p>
                      ) : null}
                    </>
                  )}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-1 font-medium text-sm leading-none">
                  <span>ຄວາມຍາວ (ນາທີ)</span>
                  <span aria-hidden className="text-red-500">
                    *
                  </span>
                </div>
                <RHF.Controller
                  name="slotDurationMinutes"
                  control={methods.control}
                  render={({ field, fieldState }) => {
                    const raw = Number(field.value);
                    const safe = PRIVATE_LESSON_SLOT_DURATION_SET.has(raw)
                      ? raw
                      : 60;
                    return (
                      <>
                        <SimpleSelect
                          value={String(safe)}
                          onValueChange={(val) => {
                            const m = Number(val);
                            if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(m))
                              return;
                            field.onChange(m);
                          }}
                          options={PRIVATE_LESSON_DURATION_SELECT_OPTIONS}
                          className="h-9 w-full"
                          placeholder="ເລືອກຄວາມຍາວ"
                        />
                        {fieldState.error?.message ? (
                          <p className="text-red-500 text-xs">
                            {fieldState.error.message}
                          </p>
                        ) : null}
                      </>
                    );
                  }}
                />
              </div>
              <div className="space-y-2 lg:col-span-2">
                <div className="font-medium text-muted-foreground text-sm leading-none">
                  ເວລາສິ້ນສຸດ <span className="text-xs">(ຄຳນວນອັດຕະໂນມັດ)</span>
                </div>
                <div className="flex h-9 w-full items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                  {computedEndTime || "—"}
                </div>
              </div>
            </div>

            <FormInput
              name="title"
              label="ຊື່ / ວັດຖຸປະສົງການຈອງ"
              placeholder="ຕົວຢ່າງ: ປະຊຸມ, ຝຶກຊ້ອມ"
              className="sm:col-span-2"
            />
            <FormInput name="contactName" label="ຊື່ຜູ້ຈອງ" placeholder="ຊື່ຜູ້ຕິດຕໍ່" />
            <FormInput
              name="contactPhone"
              label="ເບີໂທ"
              placeholder="ເບີໂທຜູ້ຈອງ"
            />

            <div className="sm:col-span-2">
              <RHF.Controller
                name="chargeType"
                control={methods.control}
                render={({ field }) => (
                  <RoomBookingChargeTypeRadio
                    value={field.value as RoomBookingChargeType}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>

            {chargeType === "paid" && (
              <>
                <FormPrice
                  name="amount"
                  label="ຈຳນວນເງິນ (ກີບ)"
                  currencySymbol="₭"
                  requiredMark
                />
                {mode === "create" && (
                  <div className="space-y-4 sm:col-span-2">
                    <RHF.Controller
                      name="settlementMode"
                      control={methods.control}
                      render={({ field }) => (
                        <SellPackageSettlementModeRadio
                          value={field.value as SellPackageSettlementMode}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                    {settlementMode === "credit" &&
                      amountW != null &&
                      amountW > 0 && (
                        <>
                          <FormPrice
                            name="amountPaidNow"
                            label="ຈຳນວນທີ່ຮັບຕອນຈອງ — 0 ຖ້າຍັງບໍ່ຮັບ"
                            placeholder="0"
                            currencySymbol="₭"
                          />
                          <p className="text-muted-foreground text-xs">
                            ລວມ{" "}
                            {amountW.toLocaleString("lo-LA")} ₭ · ຮັບແລ້ວ{" "}
                            {(amountPaidNowW ?? 0).toLocaleString("lo-LA")} ₭ ·
                            ຄ້າງ{" "}
                            {Math.max(
                              0,
                              amountW - (amountPaidNowW ?? 0),
                            ).toLocaleString("lo-LA")}{" "}
                            ₭
                          </p>
                        </>
                      )}
                  </div>
                )}
              </>
            )}

            {room?.capacity != null && (
              <p className="text-muted-foreground text-sm sm:col-span-2">
                ຄວາມຈຸຫ້ອງ: {room.capacity} ຄົນ
              </p>
            )}

            {mode === "edit" && (
              <FormTextarea
                name="changeReason"
                label="ເຫດຜົນການປ່ຽນແປງ"
                placeholder="ກຳນົດເຫດຜົນຖ້າມີການປ່ຽນແປງເວລາ / ຫ້ອງ (ບໍ່ບັງຄັບ)"
                className="sm:col-span-2"
                rows={2}
                requiredMark
              />
            )}
            {mode === "create" && (
              <div className="sm:col-span-2">
                <AssistInstructorMultiSelect
                  value={assistInstructors}
                  onChange={setAssistInstructors}
                  queryKeySuffix="room-booking-create"
                  hint="ເລືອກຄູໃນລະບົບທີ່ຈະມາຊ່ວຍ · ປ້ອນລາຄາຕໍ່ຄັ້ງ (ກີບ) ເພື່ອລວມໃນບິນ"
                />
              </div>
            )}

            <FormTextarea
              name="notes"
              label="ໝາຍເຫດ"
              placeholder="ໝາຍເຫດເພີ່ມເຕີມ"
              className="sm:col-span-2"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="submit" isLoading={isCheckingConflict || submitting}>
              {isCheckingConflict
                ? "ກຳລັງກວດສອບ..."
                : submitting
                  ? "ກຳລັງບັນທຶກ..."
                  : mode === "create"
                    ? "ສ້າງການຈອງ"
                    : "ບັນທຶກ"}
            </Button>
          </div>
        </FormRoot>
      </div>

      <div className="lg:col-span-1">
        <div className="space-y-6">
          {conflictResult?.hasConflict && (
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangleIcon className="size-5 text-red-600" />
                  <CardTitle className="text-red-900 dark:text-red-100">
                    ພົບການຂັດແຍ້ງ
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <AlertDescription className="text-red-900 dark:text-red-100">
                  ຫ້ອງທີ່ເລືອກ <strong>{room?.name ?? ""}</strong> ກຳລັງຖືກໃຊ້ງານໃນ
                  ເວລາທີ່ຮ້ອງຂໍ.
                </AlertDescription>

                {conflictResult.conflictingSessions.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-semibold text-red-900 text-sm uppercase dark:text-red-100">
                      ການຈອງທີ່ຊ້ຳກັນ
                    </p>
                    {conflictResult.conflictingSessions.map((session) => (
                      <div
                        key={session.id}
                        className="rounded-lg border border-red-200 bg-white p-3 dark:bg-gray-900"
                      >
                        <p className="font-semibold">{session.templateName}</p>
                        <div className="mt-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <ClockIcon className="size-3" />
                          <span>
                            {formatTime(session.startTime)} -{" "}
                            {formatTime(session.endTime)}
                          </span>
                        </div>
                        {session.instructorNames?.length ? (
                          <div className="mt-1 flex items-center gap-2 text-muted-foreground text-xs">
                            <UserIcon className="size-3" />
                            <span>
                              ຄູຜູ້ສອນ: {session.instructorNames.join(", ")}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  variant="outline"
                  type="button"
                  className="w-full border-red-300 text-red-700 hover:bg-red-100 dark:text-red-300"
                  onClick={handleFindAlternatives}
                  disabled={findAlternatives.isPending || isCheckingConflict}
                >
                  {findAlternatives.isPending
                    ? "ກຳລັງຄົ້ນຫາ..."
                    : "ແນະນຳຫ້ອງທາງເລືອກ"}
                </Button>

                {showAlternatives &&
                  findAlternatives.data?.rooms &&
                  findAlternatives.data.rooms.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-semibold text-sm">ຫ້ອງທີ່ມີຢູ່:</p>
                      {findAlternatives.data.rooms.map((altRoom) => (
                        <Button
                          key={altRoom.id}
                          type="button"
                          variant="outline"
                          className="w-full justify-start"
                          onClick={() =>
                            handleSelectAlternativeRoom(altRoom.id)
                          }
                        >
                          <MapPinIcon className="mr-2 size-4" />
                          {altRoom.name}
                          {altRoom.capacity != null &&
                            ` (${altRoom.capacity} ຄົນ)`}
                        </Button>
                      ))}
                    </div>
                  )}

                {showAlternatives &&
                  findAlternatives.data?.rooms &&
                  findAlternatives.data.rooms.length === 0 && (
                    <Alert>
                      <AlertDescription>
                        ບໍ່ມີຫ້ອງທາງເລືອກທີ່ມີຢູ່ສຳລັບເວລານີ້.
                      </AlertDescription>
                    </Alert>
                  )}
              </CardContent>
            </Card>
          )}

          <Card className="border-border">
            <CardContent className="space-y-4 pt-6">
              <CardTitle className="font-semibold text-base">ສະຫຼຸບ</CardTitle>
              <div className="space-y-3">
                {date && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <Calendar className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ວັນທີ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {formatDate(date)}
                      </p>
                    </div>
                  </div>
                )}
                {startTime && computedEndTime && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <ClockIcon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ເວລາ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {formatTime(startTime)} - {formatTime(computedEndTime)}
                      </p>
                    </div>
                  </div>
                )}
                {durationLabel && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <Timer className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ໄລຍະເວລາ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {durationLabel}
                      </p>
                    </div>
                  </div>
                )}
                {roomName && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <MapPinIcon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ຫ້ອງ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">{roomName}</p>
                      {room?.capacity != null && (
                        <p className="mt-1 text-muted-foreground text-xs">
                          ຄວາມຈຸ: {room.capacity} ຄົນ
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {(title || contactName) && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <MapPinIcon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ຊື່ / ວັດຖຸປະສົງ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {title || contactName || "-"}
                      </p>
                    </div>
                  </div>
                )}
                {billingTotal > 0 && (
                  <div className="space-y-2 border-t pt-3">
                    <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      ຍອດຊຳລະ
                    </p>
                    {roomAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>ຄ່າຈອງຫ້ອງ</span>
                        <span>{roomAmount.toLocaleString("lo-LA")} ກີບ</span>
                      </div>
                    )}
                    {assistInstructors
                      .filter((a) => a.pricePerSession > 0)
                      .map((a) => (
                        <div
                          key={a.id}
                          className="flex justify-between text-sm"
                        >
                          <span className="truncate pr-2">
                            ຄູຊ່ວຍ {a.name}
                          </span>
                          <span className="shrink-0">
                            {a.pricePerSession.toLocaleString("lo-LA")} × 1
                          </span>
                        </div>
                      ))}
                    <div className="flex justify-between border-t pt-2 font-semibold text-sm">
                      <span>ລວມ</span>
                      <span>{billingTotal.toLocaleString("lo-LA")} ກີບ</span>
                    </div>
                  </div>
                )}
              </div>
              {!date && !startTime && !roomName && !title && !contactName && (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground text-sm">
                    ຕື່ມຂໍ້ມູນໃນຟອມເພື່ອເບິ່ງສະຫຼຸບ
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
